import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, Loader2, Upload, History, Layers, FolderPlus } from "lucide-react";
import { toast } from "sonner";
import { api, download, errMsg, fmtDate } from "@/lib/api";
import { TYPE_LABELS, EPISTEMIC } from "@/lib/i18n";
import { useSearchStream } from "@/hooks/useSearchStream";
import { PageHeader, ResultCard, StatusDot, Empty } from "@/components/common";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";

export function DossierPicker({ value, onChange, testid = "dossier-picker" }) {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/dossiers").then((r) => setList(r.data)); }, []);
  return (
    <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? null : v)}>
      <SelectTrigger className="w-64 h-9" data-testid={testid}><SelectValue placeholder="Dossier" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="none">Aucun dossier</SelectItem>
        {list.map((d) => <SelectItem key={d.id} value={d.id}>{d.reference} — {d.title.slice(0, 40)}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export function ConnectorProgress({ state }) {
  const done = Object.keys(state.connectors).length;
  return (
    <div className="panel p-4" data-testid="search-connector-progress">
      <div className="flex items-center justify-between mb-2">
        <span className="eyebrow">Connecteurs {done}/{state.total}</span>
        {state.running && <Loader2 size={14} className="animate-spin text-primary" />}
      </div>
      <div className="h-1 rounded bg-muted overflow-hidden mb-3"><div className="h-full bg-primary transition-all" style={{ width: `${state.total ? (done / state.total) * 100 : 0}%` }} /></div>
      <div className="flex flex-wrap gap-1.5">
        {Object.entries(state.connectors).map(([k, c]) => (
          <span key={k} title={c.error || ""} className="text-[10px] font-mono px-2 py-1 rounded border flex items-center gap-1.5" data-testid={`connector-status-${k}`}>
            <StatusDot status={c.status} />{k}<span className="text-muted-foreground">{c.count} · {c.ms}ms</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function ResultsView({ results, searchId }) {
  const [ft, setFt] = useState("all");
  const [fe, setFe] = useState("all");
  const [minRel, setMinRel] = useState(0);
  const [text, setText] = useState("");
  const types = useMemo(() => [...new Set(results.map((r) => r.info_type))], [results]);
  const filtered = results.filter((r) => (ft === "all" || r.info_type === ft) && (fe === "all" || r.epistemic === fe) && r.reliability >= minRel &&
    (!text || `${r.title} ${r.summary} ${r.source}`.toLowerCase().includes(text.toLowerCase())));
  return (
    <div className="grid xl:grid-cols-[240px_1fr] gap-4">
      <div className="panel p-4 space-y-4 h-fit xl:sticky xl:top-20" data-testid="search-filters">
        <div className="eyebrow">Filtres multicritères</div>
        <input data-testid="filter-text" placeholder="Texte…" value={text} onChange={(e) => setText(e.target.value)} className="w-full h-8 px-2 rounded border bg-background text-xs" />
        <Select value={ft} onValueChange={setFt}><SelectTrigger className="h-8 text-xs" data-testid="filter-info-type"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tous types</SelectItem>{types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
        <Select value={fe} onValueChange={setFe}><SelectTrigger className="h-8 text-xs" data-testid="filter-epistemic"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Toutes qualifications</SelectItem>{Object.entries(EPISTEMIC).map(([k, v]) => <SelectItem key={k} value={k}>{v.fr}</SelectItem>)}</SelectContent></Select>
        <div><div className="text-[11px] mb-2">Fiabilité min. : <span className="font-mono text-primary">{minRel}</span></div><Slider data-testid="filter-reliability" value={[minRel]} max={100} step={5} onValueChange={(v) => setMinRel(v[0])} /></div>
        {searchId && <div className="flex gap-2 pt-2 border-t">
          <button data-testid="export-results-csv" onClick={() => download(`/results/export?search_id=${searchId}&fmt=csv`, `madengi_${searchId.slice(0, 8)}.csv`)} className="flex-1 h-8 rounded border text-xs flex items-center justify-center gap-1 hover:border-primary"><Download size={12} />CSV</button>
          <button data-testid="export-results-json" onClick={() => download(`/results/export?search_id=${searchId}&fmt=json`, `madengi_${searchId.slice(0, 8)}.json`)} className="flex-1 h-8 rounded border text-xs flex items-center justify-center gap-1 hover:border-primary"><Download size={12} />JSON</button>
        </div>}
      </div>
      <div className="space-y-3" data-testid="universal-search-results">
        <div className="text-xs text-muted-foreground font-mono">{filtered.length} / {results.length} résultats</div>
        {filtered.map((r, i) => <ResultCard key={r.id} r={r} idx={i} />)}
        {!filtered.length && <Empty>Aucun résultat correspondant.</Empty>}
      </div>
    </div>
  );
}

function LiveSearch() {
  const [params] = useSearchParams();
  const q = params.get("q") || "";
  const [type, setType] = useState(params.get("type") || "auto");
  const [dossier, setDossier] = useState(null);
  const [state, run] = useSearchStream();
  useEffect(() => { if (q) run(q, { type: type === "auto" ? undefined : type, dossierId: dossier }); // eslint-disable-next-line
  }, [q, type]);
  const attach = async () => {
    if (!dossier || !state.searchId) return toast.error("Sélectionnez un dossier");
    const r = await api.post(`/dossiers/${dossier}/attach-search/${state.searchId}`);
    toast.success(`${r.data.attached} résultats rattachés au dossier`);
  };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="font-mono text-lg" data-testid="search-current-query">« {q || "—"} »</div>
        {state.type && <span className="text-xs px-2 py-1 rounded bg-primary/15 text-primary border border-primary/30" data-testid="search-detected-type">{TYPE_LABELS[state.type]}</span>}
        <Select value={type} onValueChange={setType}><SelectTrigger className="w-56 h-9" data-testid="search-type-override"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="auto">Détection automatique</SelectItem>{Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
        <div className="ml-auto flex items-center gap-2"><DossierPicker value={dossier} onChange={setDossier} />
          <button data-testid="attach-search-to-dossier" onClick={attach} className="h-9 px-3 rounded-md border text-sm flex items-center gap-2 hover:border-primary"><FolderPlus size={14} />Rattacher</button></div>
      </div>
      {state.alternatives?.length > 0 && <div className="text-xs text-muted-foreground">Interprétations alternatives : {state.alternatives.map((a) => <button key={a} onClick={() => setType(a)} className="ml-2 underline hover:text-primary" data-testid={`alt-type-${a}`}>{TYPE_LABELS[a]}</button>)}</div>}
      {state.error && <div className="text-sm text-rose-500" data-testid="search-error">{state.error}</div>}
      {!q ? <Empty>Saisissez une donnée dans la barre de recherche universelle (Ctrl+K).</Empty> : <>
        <ConnectorProgress state={state} />
        <ResultsView results={state.results} searchId={state.searchId} />
      </>}
    </div>
  );
}

function Batch() {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState(null);
  const [dossier, setDossier] = useState(null);
  const onFile = async (f) => {
    const fd = new FormData(); fd.append("file", f);
    try { const r = await api.post("/search/import", fd); setText(r.data.queries.join("\n")); toast.success(`${r.data.queries.length} requêtes importées`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const runBatch = async () => {
    setBusy(true);
    try { const r = await api.post("/search/batch", { queries: text.split("\n"), dossier_id: dossier }); setOut(r.data.items); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <div className="panel p-4 space-y-3">
        <div className="eyebrow">Traitement par lots (50 max · 4 en parallèle)</div>
        <textarea data-testid="batch-textarea" value={text} onChange={(e) => setText(e.target.value)} rows={12} placeholder={"une requête par ligne\nexemple.com\n8.8.8.8\nCVE-2024-3400"} className="w-full p-3 rounded border bg-background font-mono text-xs" />
        <div className="flex flex-wrap gap-2 items-center">
          <label className="h-9 px-3 rounded-md border text-sm flex items-center gap-2 cursor-pointer hover:border-primary"><Upload size={14} />Importer CSV / JSON
            <input data-testid="batch-import-input" type="file" accept=".csv,.json,.txt" className="hidden" onChange={(e) => e.target.files[0] && onFile(e.target.files[0])} /></label>
          <DossierPicker value={dossier} onChange={setDossier} testid="batch-dossier-picker" />
          <button data-testid="batch-run-button" disabled={busy || !text.trim()} onClick={runBatch} className="h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm flex items-center gap-2 disabled:opacity-50">{busy && <Loader2 size={14} className="animate-spin" />}Lancer le lot</button>
        </div>
      </div>
      <div className="panel p-4" data-testid="batch-results">
        <div className="eyebrow mb-3">Bilan du lot</div>
        {(out || []).map((o) => (
          <div key={o.search_id} className="flex items-center justify-between py-2 border-b text-xs font-mono">
            <span>{o.query}</span><span className="text-muted-foreground">{TYPE_LABELS[o.type]}</span><span className="text-primary">{o.result_count} résultats</span>
            <button onClick={() => download(`/results/export?search_id=${o.search_id}&fmt=csv`, `${o.query}.csv`)} className="underline">CSV</button>
          </div>
        ))}
        {!out && <div className="text-xs text-muted-foreground">Aucun lot exécuté.</div>}
      </div>
    </div>
  );
}

function HistoryTab() {
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(null);
  const [res, setRes] = useState([]);
  useEffect(() => { api.get("/searches", { params: { limit: 100 } }).then((r) => setList(r.data)); }, []);
  const open = async (s) => { setSel(s); const r = await api.get(`/searches/${s.id}/results`); setRes(r.data); };
  return (
    <div className="grid xl:grid-cols-[320px_1fr] gap-4">
      <div className="panel max-h-[70vh] overflow-auto" data-testid="search-history-list">
        {list.map((s) => (
          <button key={s.id} onClick={() => open(s)} data-testid={`history-item-${s.id}`} className={`w-full text-left px-4 py-3 border-b hover:bg-accent ${sel?.id === s.id ? "bg-primary/10" : ""}`}>
            <div className="font-mono text-sm truncate">{s.query}</div>
            <div className="text-[10px] text-muted-foreground">{TYPE_LABELS[s.type]} · {s.result_count} résultats · {fmtDate(s.created_at)}{s.batch ? " · lot" : ""}</div>
          </button>
        ))}
      </div>
      <div>{sel ? <ResultsView results={res} searchId={sel.id} /> : <Empty>Sélectionnez une recherche de l'historique.</Empty>}</div>
    </div>
  );
}

export default function SearchPage() {
  return (
    <div>
      <PageHeader eyebrow="MADENGI SEARCH" title="Recherche multisource" desc="Détection automatique du type de donnée, interrogation parallèle des connecteurs autorisés et affichage progressif des résultats. Une correspondance n'est jamais une identification certaine." />
      <Tabs defaultValue="live">
        <TabsList className="mb-4"><TabsTrigger value="live" data-testid="search-tab-live">Recherche</TabsTrigger><TabsTrigger value="batch" data-testid="search-tab-batch"><Layers size={13} className="mr-1" />Lots & import</TabsTrigger><TabsTrigger value="history" data-testid="search-tab-history"><History size={13} className="mr-1" />Historique</TabsTrigger></TabsList>
        <TabsContent value="live"><LiveSearch /></TabsContent>
        <TabsContent value="batch"><Batch /></TabsContent>
        <TabsContent value="history"><HistoryTab /></TabsContent>
      </Tabs>
    </div>
  );
}
